import React, { useEffect, useState } from 'react';
import StatCard from '../../components/StatCard';
import CreatividadNombre from './CreatividadNombre';
import useFetch from '../../hooks/useFetch';
import styles from '../../styles/dashboard.module.css';

const BASE_DASHBOARD = '/dashboard';

const CREATIVIDAD_CLASS_BY_ID = {
    1: styles.sabio,
    2: styles.reina,
    3: styles.artesano,
    4: styles.aprendiz,
};

const CREATIVIDAD_CLASS = {
    Sabio: styles.sabio,
    Reina: styles.reina,
    Artesano: styles.artesano,
    Aprendiz: styles.aprendiz,
    Artesana: styles.artesano,
    Sentido: styles.sabio,
    Vínculo: styles.reina,
    Vinculo: styles.reina,
    Acción: styles.artesano,
    Accion: styles.artesano,
    Exploración: styles.aprendiz,
    Exploracion: styles.aprendiz,
};

const formatNum = (n) => (n == null ? '—' : Number(n).toLocaleString('es-ES'));

const DashboardTab = () => {
    const { execute } = useFetch();
    const [stats, setStats] = useState(null);
    const [loadingResumen, setLoadingResumen] = useState(true);
    const [loadingCreatividades, setLoadingCreatividades] = useState(true);
    const [creatividadesCached, setCreatividadesCached] = useState(false);
    const [error, setError] = useState(null);

    useEffect(() => {
        let cancelled = false;
        setLoadingResumen(true);
        setLoadingCreatividades(true);
        setError(null);

        const loadResumen = async () => {
            try {
                const res = await execute(`${BASE_DASHBOARD}/stats/resumen`, 'GET');
                if (!cancelled) {
                    setStats((prev) => ({ ...(prev || {}), ...(res?.data ?? {}) }));
                }
            } catch (err) {
                if (!cancelled) {
                    setStats(null);
                    setError(err?.message || 'No se pudieron cargar las estadísticas');
                }
            } finally {
                if (!cancelled) setLoadingResumen(false);
            }
        };

        const loadCreatividades = async () => {
            try {
                const res = await execute(`${BASE_DASHBOARD}/stats/creatividades`, 'GET');
                if (!cancelled) {
                    setStats((prev) => ({
                        ...(prev || {}),
                        creatividades: res?.data?.creatividades ?? null,
                        total_preguntas: res?.data?.total_preguntas ?? prev?.total_preguntas,
                    }));
                    setCreatividadesCached(Boolean(res?.data?.cached));
                }
            } catch {
                if (!cancelled) {
                    setStats((prev) => (prev ? { ...prev, creatividades: null } : prev));
                }
            } finally {
                if (!cancelled) setLoadingCreatividades(false);
            }
        };

        loadResumen();
        loadCreatividades();

        return () => { cancelled = true; };
    }, [execute]);

    const creatividades = stats?.creatividades?.medias_completados ?? [];

    if (loadingResumen && !stats) {
        return <p className={styles.loadingMsg}>Cargando estadísticas…</p>;
    }

    if (error && !stats) {
        return <p className={styles.errorMsg}>{error}</p>;
    }

    const u = stats?.usuarios ?? {};
    const r = stats?.respuestas ?? {};
    const p = r?.participaciones ?? {};

    return (
        <>
            <section className={styles.dashboardGrid}>
                <StatCard
                    title="Usuarios registrados"
                    value={formatNum(u.total)}
                    subValue={
                        <span>
                            Con respuestas: <strong>{formatNum(u.con_respuestas)}</strong>
                        </span>
                    }
                />
                <StatCard
                    title="Test completados"
                    value={formatNum(p.completadas)}
                    subValue={
                        <span>
                            En progreso: <strong>{formatNum(p.en_progreso)}</strong>
                            {' · '}
                            Sin iniciar: <strong>{formatNum(p.no_iniciadas)}</strong>
                        </span>
                    }
                />
                <StatCard
                    title="Respuestas este mes"
                    value={formatNum(r.este_mes)}
                    subValue={
                        <span>
                            Este año: <strong>{formatNum(r.este_ano)}</strong>
                            {' · '}
                            Total registros: <strong>{formatNum(r.total_registros)}</strong>
                        </span>
                    }
                    isLive={true}
                />
            </section>

            <section className={styles.section}>
                <h2 className={styles.sectionTitle}>Porcentaje por creatividad</h2>
                <p className={styles.sectionDesc}>
                    Porcentaje de tests completados ({stats?.total_preguntas ?? 40} preguntas) en los que cada creatividad es la predominante
                    {loadingCreatividades
                        ? ' (calculando…)'
                        : ` (${formatNum(stats?.creatividades?.participaciones_completas_analizadas)} participaciones${creatividadesCached ? ', datos en caché' : ''}).`}
                </p>
                {loadingCreatividades ? (
                    <p className={styles.loadingMsg}>Calculando distribución por creatividad…</p>
                ) : creatividades.length === 0 ? (
                    <p className={styles.emptyHint}>Aún no hay tests completados para calcular porcentajes.</p>
                ) : (
                    <div className={styles.creatividadesGrid}>
                        {creatividades.map((c) => {
                            const clase =
                                CREATIVIDAD_CLASS_BY_ID[c.id_creatividad] ||
                                CREATIVIDAD_CLASS[c.nombre] ||
                                '';
                            const pct = c.porcentaje_predominante ?? 0;
                            return (
                                <div key={c.id_creatividad} className={styles.creatividadCard}>
                                    <div className={styles.creatividadHeader}>
                                        <div className={`${styles.creatividadLabel} ${clase}`}>
                                            <CreatividadNombre creatividad={c} variant="card" />
                                        </div>
                                        <span className={styles.porcentajeValor}>{pct}%</span>
                                    </div>
                                    <div className={styles.barTrack}>
                                        <div
                                            className={`${styles.barFill} ${clase}`}
                                            style={{ width: `${pct}%` }}
                                        />
                                    </div>
                                    <p className={styles.creatividadMeta}>
                                        Creatividad predominante en{' '}
                                        <strong>{formatNum(c.usuarios_predominantes)}</strong> participaciones
                                    </p>
                                </div>
                            );
                        })}
                    </div>
                )}
            </section>
        </>
    );
};

export default DashboardTab;
